import {useQuery} from '@tanstack/react-query';
import {AxiosResponse} from 'axios';
import {IApiPaginationResponse2} from '../../dto/API.dto';
import {
  IFuzzyFields,
  IMediaArtistOrSong,
  IMediaTrack,
  ITrackCategory,
} from '../../dto/MediaTrack.dto';
import {apiGet} from '../../axiosConfig';
import {
  IConditionalOperators,
  IDataGridProSearchSort,
} from '../../dto/Html.dto';
import {IDemoScriptStateProp} from '../../dto/demoStingerFulfilment.dto';

export const getMediaTracksProPaginateApiPath = (companyId: Number) => {
  return `/v1/music-selection/company/${companyId}/media-track/pro`;
};
export const getMediaTracksWithoutCompanyProPaginateApiPath = () => {
  return `/v1/music-selection/media-track/pro`;
};
export const getMediaTracksWithTokenProPaginateApiPath = (token: string) => {
  return `/v1/music-selection/walkup-student-form/${token}/media-track/pro`;
};

export const getMediaTracksProArtistOrSongApiPath = (companyId: Number) => {
  return `/v1/music-selection/company/${companyId}/media-track/artist-or-song`;
};

const getMediaTracks = (params: IMediaTracksQueryParams, companyId: number) => {
  return apiGet(getMediaTracksProPaginateApiPath(companyId), {params}).then(
    (response: AxiosResponse<IApiPaginationResponse2<IMediaTrack>>) => {
      return response.data;
    },
  );
};

const getMediaTracksWithoutCompany = (params: IMediaTracksQueryParams) => {
  return apiGet(getMediaTracksWithoutCompanyProPaginateApiPath(), {
    params,
  }).then((response: AxiosResponse<IApiPaginationResponse2<IMediaTrack>>) => {
    return response.data;
  });
};

const getMediaTracksWithToken = (
  token: string,
  params: IMediaTracksQueryParams,
) => {
  return apiGet(getMediaTracksWithTokenProPaginateApiPath(token), {
    params,
  }).then((response: AxiosResponse<IApiPaginationResponse2<IMediaTrack>>) => {
    return response.data;
  });
};

export type IMediaTracksQueryParams = IDataGridProSearchSort & {
  starts_with?: string;
  track_category?: ITrackCategory;
  track_name?: string;
  track_artist?: string;
  fuzzy?: boolean;
  genres?: number[];
  songSets?: number[];
  mediaTags?: number[];
  trackIds?: number[];
  type?: string;
  event_id?: number[];
  school_id?: number[];
  station_id?: number[];
  selection_type?: string;
} & (
    | {
        track_length: number;
        track_length_condition: IConditionalOperators;
      }
    | {track_length?: undefined; track_length_condition?: undefined}
  );

export const useRqMediaTracksPaginate = (
  query: IMediaTracksQueryParams = {page: 1, pageSize: 10},
  companyId: number,
  enabled: boolean = true,
) => {
  const params = {...query, page: query.page + 1};
  return useQuery({
    queryKey: [getMediaTracksProPaginateApiPath(companyId), params],
    queryFn: () => getMediaTracks(params, companyId),
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled: Boolean(companyId) && enabled,
    staleTime: 60000,
  });
};

export const useRqMediaTracksWithoutCompanyPaginate = (
  query: IMediaTracksQueryParams = {page: 1, pageSize: 10},
  enabled: boolean = true,
) => {
  const params = {...query, page: query.page + 1};
  return useQuery({
    queryKey: [getMediaTracksWithoutCompanyProPaginateApiPath(), params],
    queryFn: () => getMediaTracksWithoutCompany(params),
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled: enabled,
  });
};

export const useRqMediaTracksWithTokenPaginate = (
  token: string,
  query: IMediaTracksQueryParams = {page: 1, pageSize: 10},
  enabled: boolean = true,
) => {
  const params = {...query, page: query.page + 1};
  return useQuery({
    queryKey: [getMediaTracksWithTokenProPaginateApiPath(token), params],
    queryFn: () => getMediaTracksWithToken(token, params),
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled: enabled,
  });
};

/**
 *  Track Artist and Track Name are used for fuzzy search.
 */

const getMediaTracksArtistOrSong = (
  params: IMediaArtistOrSong,
  companyId: number,
) => {
  return apiGet(getMediaTracksProArtistOrSongApiPath(companyId), {params}).then(
    (
      response: AxiosResponse<IApiPaginationResponse2<IDemoScriptStateProp>>,
    ) => {
      return response.data;
    },
  );
};
export const useRqMediaTracksArtistOrSong = (
  field: IFuzzyFields,
  field_value: string,
  alternate_field_value: string,
  companyId: number,
  enabled: boolean = true,
) => {
  const params = {field, field_value, alternate_field_value};
  return useQuery({
    queryKey: [getMediaTracksProArtistOrSongApiPath(companyId), params],
    queryFn: () => getMediaTracksArtistOrSong(params, companyId),
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled: Boolean(companyId) && enabled,
    staleTime: 60000,
  });
};
